import { Link } from "@remix-run/react";
import { IconDots } from "@tabler/icons-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { Icon } from "#app/components/ui/icon";
import { RailTooltip } from "#app/components/ui/tooltip";
import { cn } from "#app/utils/misc.tsx";
import { useOptionalUser } from "#app/utils/user";
import buildLinks, { PRIMARY_NAV_COUNT } from "../links";
import { isGeneralNavLinkActive } from "../nav-active";

/**
 * Maturity flag for a nav entry (currently only Policies' "Alpha"). `floating`
 * pins it over the icon for the collapsed rail, where there is no label for it
 * to sit beside.
 */
function NavBadge({ label, floating }: { label: string; floating?: boolean }) {
  return (
    <span
      className={cn(
        "pointer-events-none flex shrink-0 items-center justify-center rounded-[4px] bg-[#535EA4] font-bold tracking-normal text-white uppercase select-none",
        floating
          ? "absolute -top-0.5 left-1/2 z-10 -translate-x-1/2 px-1 py-[1px] text-[7.5px] shadow-xs"
          : "ml-1.5 px-1.5 py-[1px] text-[8.5px]",
      )}
    >
      {label}
    </span>
  );
}

function CollapsedNavTooltip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: ReactNode;
}) {
  if (!collapsed) return <>{children}</>;
  return <RailTooltip title={label}>{children}</RailTooltip>;
}

type MenuItemsProps = {
  collapsed: boolean;
  pathname: string;
  /** Routes through the sidebar's design-phase navigation guard. */
  onNavigate: (to: string) => void;
};

export function MenuItems({ collapsed, pathname, onNavigate }: MenuItemsProps) {
  const { t } = useTranslation(["nav", "common"]);
  const user = useOptionalUser();

  if (!user) return null;

  const generalLinks = buildLinks(user, t).filter(
    (link) => link.section === "general",
  );
  const visibleItems = generalLinks.slice(0, PRIMARY_NAV_COUNT);
  const moreItems = generalLinks.slice(PRIMARY_NAV_COUNT);
  const moreIsActive = moreItems.some((item) =>
    isGeneralNavLinkActive(item.to, pathname),
  );

  return (
    <nav
      className={cn(
        "sidebar-content mt-4 transition-all",
        collapsed ? "-mx-1" : "-mx-2",
      )}
    >
      <ul className="space-y-1">
        {visibleItems.map((item) => {
          const NavIcon = item.tablerIcon;
          const isActive = isGeneralNavLinkActive(item.to, pathname);
          const label = item.badge
            ? `${item.title} (${item.badge})`
            : item.title;

          return (
            <li key={item.to}>
              <CollapsedNavTooltip collapsed={collapsed} label={label}>
                <Link
                  id={item.title.toLocaleLowerCase().replaceAll(" ", "-")}
                  to={item.to}
                  aria-label={label}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    onNavigate(item.to);
                  }}
                  className={cn(
                    "relative flex h-8 min-w-8 items-center rounded-full transition-all duration-200",
                    collapsed ? "px-1.5" : "px-2.5",
                    isActive
                      ? "bg-zinc-200/60 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100"
                      : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300",
                  )}
                >
                  {NavIcon ? (
                    <NavIcon
                      className="h-5 w-5 shrink-0 text-zinc-900 dark:text-zinc-200"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <Icon name={item.icon} className="h-5 w-5 shrink-0" />
                  )}

                  {item.badge && collapsed ? (
                    <NavBadge label={item.badge} floating />
                  ) : null}

                  <span
                    className={cn(
                      "ml-1.5 overflow-hidden text-sm whitespace-nowrap transition-all duration-200",
                      collapsed ? "w-0 opacity-0" : "w-auto opacity-100",
                      isActive && "font-medium text-black dark:text-white",
                    )}
                  >
                    {item.title}
                  </span>

                  {item.badge && !collapsed ? (
                    <NavBadge label={item.badge} />
                  ) : null}
                </Link>
              </CollapsedNavTooltip>
            </li>
          );
        })}

        {moreItems.length > 0 ? (
          <li>
            <DropdownMenu>
              <CollapsedNavTooltip collapsed={collapsed} label={t("nav:more")}>
                <DropdownMenuTrigger
                  aria-label={t("nav:more")}
                  className={cn(
                    "flex h-8 cursor-pointer items-center rounded-full transition-all duration-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300",
                    collapsed ? "w-8 px-1.5" : "px-2.5",
                    moreIsActive &&
                      "bg-zinc-200/60 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100",
                  )}
                >
                  <IconDots className="h-5 w-5 shrink-0 text-zinc-900 dark:text-zinc-200" />

                  <span
                    className={cn(
                      "ml-2 overflow-hidden text-sm whitespace-nowrap transition-all duration-200",
                      collapsed ? "w-0 opacity-0" : "w-auto opacity-100",
                    )}
                  >
                    {t("nav:more")}
                  </span>
                </DropdownMenuTrigger>
              </CollapsedNavTooltip>

              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={8}
                className="w-52"
              >
                {moreItems.map((item) => {
                  const NavIcon = item.tablerIcon;
                  const isActive = isGeneralNavLinkActive(item.to, pathname);

                  return (
                    <DropdownMenuItem
                      key={item.to}
                      onClick={() => onNavigate(item.to)}
                      className={cn(isActive && "font-medium text-primary")}
                    >
                      {NavIcon ? (
                        <NavIcon className="h-4 w-4" />
                      ) : (
                        <Icon name={item.icon} className="h-4 w-4" />
                      )}
                      <span>{item.title}</span>
                      {item.badge ? <NavBadge label={item.badge} /> : null}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}

export default MenuItems;
